import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useMe } from '../../hooks/useUsers';
import { useProjects } from '../../hooks/useProjects';
import { useAuth } from '../../auth';
import { ApiClientError } from '../../api/client';
import AccessDeniedView from '../AccessDeniedView';
import StaticTocSidebar from '../StaticTocSidebar';
import TocSidebar from '../TocSidebar';
import { TopBanner } from './TopBanner';

interface MainLayoutProps {
  children?: ReactNode;
}

/**
 * Portal shell: the [TopBanner] over the page.
 *
 * Wiki-specific UI (TOC sidebar, wiki/raw/updates nav, the LLM search box) only renders when the current
 * route is *inside* a wiki sub-project (`/<wiki-slug>/...`). On every other route those elements are hidden.
 */
export default function MainLayout({ children }: MainLayoutProps) {
  const { userEmail, logout } = useAuth();
  const { data: me, error, isLoading } = useMe();
  const { data: projects } = useProjects();
  const location = useLocation();
  const isAllowlistDenied = error instanceof ApiClientError && error.status === 403;

  const wikiSlug = currentSlug(location.pathname, projects, 'wiki');
  const staticSlug = currentSlug(location.pathname, projects, 'static');
  const isInWiki = wikiSlug !== null;
  const isInStatic = staticSlug !== null;

  return (
    <div className="app-root" data-testid="main-layout">
      <TopBanner
        me={me}
        userEmail={userEmail}
        onLogout={logout}
        wikiSlug={wikiSlug}
        showSearch={isInWiki && !isAllowlistDenied}
      />

      <div className="body-row" data-testid="main-body">
        {isInWiki && !isAllowlistDenied ? <TocSidebar /> : null}
        {isInStatic && !isAllowlistDenied && staticSlug ? <StaticTocSidebar slug={staticSlug} /> : null}
        <div className="main-scroll" data-testid="main-scroll">
          <main className="main-content" data-testid="main-content">
            {isLoading ? <p className="muted">Loading...</p> : null}
            {isAllowlistDenied ? <AccessDeniedView email={userEmail} /> : null}
            {!isLoading && !isAllowlistDenied ? children : null}
          </main>
        </div>
      </div>
    </div>
  );
}

interface ProjectLike {
  slug: string;
  type: string;
}

/** Returns the slug of the sub-project of the given type at the current path, or null if none matches. */
function currentSlug(pathname: string, projects: ProjectLike[] | undefined, type: string): string | null {
  if (!projects) return null;
  const firstSegment = pathname.split('/').filter(Boolean)[0];
  if (!firstSegment) return null;
  const match = projects.find((p) => p.slug === firstSegment && p.type === type);
  return match ? match.slug : null;
}
